import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

import { PULSE_FONT_SIZE_TOKENS } from './pulse-font-size-tokens';

// Pulse typography tokens (`text-body-sm`, `text-heading-md`, etc.) are font-size
// utilities, but tailwind-merge doesn't know them and defaults them into the
// text-color group, so a `text-body-sm text-text-inverse` pair collapses and the
// colour is silently dropped. Register them as font-size so size and colour coexist.
// `bg-brand-fill` has the same problem: it sets background-image, but would be read
// as a background colour and evict the `bg-brand-primary` fallback paired with it.
// `shadow-up` hits the same trap in the shadow-colour group, so a base `shadow-lg` would survive it.
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [{ text: [...PULSE_FONT_SIZE_TOKENS] }],
      'bg-image': ['bg-brand-fill'],
      shadow: [{ shadow: ['up', 'top-bar', 'card-accent', 'card-summary', 'card-tile', 'card-banner', 'card-option', 'segment', 'footer', 'focus-primary', 'focus-gray', 'focus-error', 'focus-warning', 'focus-success', 'focus-primary-offset', 'focus-error-offset', 'focus-primary-inset'] }],
    },
  },
});

/**
 * Combines multiple class values using clsx and twMerge utilities.
 *
 * This function takes any number of class values (strings, objects, arrays)
 * and processes them with clsx to resolve conditional classes, then passes
 * the result through twMerge to handle Tailwind CSS class conflicts.
 *
 * @param inputs - Any number of class values to be combined
 * @returns A merged string of CSS classes with conflicts resolved
 */
function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export default cn;
